// Copyright (c) Ping. All rights reserved.
//
// KLIPY v1 REST adapter for the chat-room media picker. Wraps the
// public KLIPY API (`https://api.klipy.com/api/v1/<key>/...`) behind
// the shared `GifSearchProvider` + `StickerSearchProvider` contracts
// so the unified emoji/GIF/sticker popover (Figma `2804:26810` /
// `2804:27348`) can render trending + keyword-driven results.
//
// Why KLIPY: Google announced on 2026-01-14 that the Tenor API is
// being sunset on 2026-06-30. KLIPY was founded by ex-Tenor team
// members and exposes a nearly identical surface, so migrating from
// Tenor is mostly a base-URL + auth-shape swap.
//
// API key is read from `VITE_KLIPY_API_KEY` at Vite build time. The
// key goes into the URL path (not a header) per KLIPY's convention:
//   https://api.klipy.com/api/v1/<API_KEY>/gifs/trending
// When the key is missing we return the shared empty providers so
// the panel still renders gracefully (empty state, no errors).

import {
  emptyGifProvider,
  emptyStickerProvider,
  type GifItem,
  type GifSearchProvider,
  type StickerSearchProvider,
} from '@ping/ui';

import { log } from '@core/log/log';

const KLIPY_BASE = 'https://api.klipy.com/api/v1';

// One-shot invalid-key latch. KLIPY returns the same auth error for
// every request when the key is invalid; we log once and then
// short-circuit subsequent calls so the console doesn't flood.
let keyValid: boolean | null = null;

// Stable per-browser customer id. KLIPY's trending/search endpoints
// take a `customer_id` query param so they can return personalized
// results across requests. We don't have a Ping user id available
// in this layer (the chat surface is upstream of auth identity), so
// we mint a uuid-shaped string the first time the picker opens and
// persist it to localStorage. Subsequent loads reuse the same id so
// KLIPY's personalization state survives reloads.
const KLIPY_CUSTOMER_KEY = 'com.ping.web.klipy.customer';
function readCustomerId(): string {
  try {
    const stored = localStorage.getItem(KLIPY_CUSTOMER_KEY);
    if (typeof stored === 'string' && stored.length > 0) return stored;
    const minted =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(KLIPY_CUSTOMER_KEY, minted);
    return minted;
  } catch {
    // SSR / privacy mode — return a transient id. KLIPY accepts any
    // opaque string here.
    return `c-${Date.now().toString(36)}`;
  }
}

// Best-effort locale tag for KLIPY's localized result ranking. We
// read `navigator.language` (e.g. `en-US`, `de-DE`); falls back to
// `en-US` when the platform doesn't expose one.
function readLocale(): string {
  try {
    const lang =
      typeof navigator !== 'undefined' && typeof navigator.language === 'string'
        ? navigator.language
        : '';
    if (lang.length > 0) return lang;
  } catch {
    // fall through
  }
  return 'en-US';
}

/**
 * Tries `VITE_KLIPY_API_KEY` first, then falls back to
 * `VITE_TENOR_API_KEY` for hosts mid-migration that still ship the
 * Tenor key under the legacy env var.
 */
function readKlipyKey(): string | null {
  try {
    const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
    const k = env['VITE_KLIPY_API_KEY'];
    if (typeof k === 'string' && k.length > 0) return k;
    // Soft-fallback: if only the Tenor key is set we still try
    // KLIPY with it — same dev may have one key for testing. The
    // first request flips the latch when it 401s.
    const legacy = env['VITE_TENOR_API_KEY'];
    if (typeof legacy === 'string' && legacy.length > 0) return legacy;
  } catch {
    // fall through
  }
  return null;
}

function clampLimit(limit: number | undefined): number {
  if (typeof limit !== 'number' || !Number.isFinite(limit)) return 24;
  return Math.max(1, Math.min(50, Math.trunc(limit)));
}

/**
 * Build the `?...` portion of a KLIPY request URL with the four
 * params the public API documents: `page`, `per_page`, `customer_id`,
 * `locale`. `q` is added on the search endpoints; the same helper is
 * reused for both trending + search to keep them consistent.
 */
function buildKlipyQuery(opts: {
  readonly limit: number;
  readonly q?: string | undefined;
  readonly page?: number | undefined;
}): string {
  const params = new URLSearchParams();
  params.set('page', String(opts.page ?? 1));
  params.set('per_page', String(opts.limit));
  params.set('customer_id', readCustomerId());
  params.set('locale', readLocale());
  if (opts.q !== undefined && opts.q.length > 0) {
    params.set('q', opts.q);
  }
  return params.toString();
}

interface KlipyMediaFile {
  readonly url?: string;
  readonly width?: number;
  readonly height?: number;
  readonly size?: number;
}

// KLIPY's actual response shape (verified live, 2026-05-13):
//   data: [
//     {
//       id, slug, title,
//       file: {
//         hd: { gif: {url,width,height,size}, webp: {...}, webm: {...}, png: {...} },
//         md: { gif, webp, webm, png },
//         sm: { gif, webp, webm, png },
//         xs: { gif, webp, webm, png }
//       },
//       type: "sticker" | "gif",
//       blur_preview: "data:image/jpeg;base64,..."
//     }
//   ]
// We tolerate `data` being either an array directly or wrapped in
// `data.data` / `data.results` / `data.items` for forward-compat.
type KlipySizeKey = 'hd' | 'md' | 'sm' | 'xs';
type KlipyFormatKey = 'gif' | 'webp' | 'webm' | 'png' | 'mp4';
type KlipySizeMap = Partial<Readonly<Record<KlipyFormatKey, KlipyMediaFile>>>;
interface KlipyItem {
  readonly id?: string | number;
  readonly slug?: string;
  readonly title?: string;
  readonly description?: string;
  readonly type?: string;
  readonly blur_preview?: string;
  readonly file?: Partial<Readonly<Record<KlipySizeKey, KlipySizeMap>>>;
  // Legacy flat fallbacks (older API surfaces).
  readonly url?: string;
  readonly preview_url?: string;
  readonly width?: number;
  readonly height?: number;
}

interface KlipyEnvelope {
  readonly result?: boolean;
  readonly data?:
    | {
        readonly data?: readonly KlipyItem[];
        readonly results?: readonly KlipyItem[];
        readonly items?: readonly KlipyItem[];
      }
    | readonly KlipyItem[];
  readonly errors?: { readonly message?: readonly string[] };
}

/**
 * Walk `file[size][format].url` in preference order — first match
 * wins. Sizes are tried before formats so we don't pick a `webm`
 * from the `hd` bucket when a `gif` from `sm` would have been a
 * better choice for the grid thumbnail.
 */
function pickFile(
  item: KlipyItem,
  sizes: readonly KlipySizeKey[],
  formats: readonly KlipyFormatKey[],
): KlipyMediaFile | null {
  const fileMap = item.file;
  if (fileMap !== undefined) {
    for (const size of sizes) {
      const sizeMap = fileMap[size];
      if (sizeMap === undefined) continue;
      for (const fmt of formats) {
        const file = sizeMap[fmt];
        if (file !== undefined && typeof file.url === 'string' && file.url.length > 0) {
          return file;
        }
      }
    }
  }
  return null;
}

function toGifItem(entry: KlipyItem): GifItem | null {
  // Preview thumbnail — prefer small static gif for the grid so the
  // popover loads fast. `webp` is a fine fallback.
  const preview =
    pickFile(entry, ['sm', 'xs', 'md', 'hd'], ['gif', 'webp']) ??
    (typeof entry.preview_url === 'string'
      ? ({
          url: entry.preview_url,
          width: entry.width,
          height: entry.height,
        } as KlipyMediaFile)
      : null);
  // Full-resolution payload used for the actual send — prefer
  // medium/HD gif so the receiving bubble has a clean asset.
  const full =
    pickFile(entry, ['md', 'hd', 'sm', 'xs'], ['gif', 'webp', 'mp4']) ??
    (typeof entry.url === 'string'
      ? ({
          url: entry.url,
          width: entry.width,
          height: entry.height,
        } as KlipyMediaFile)
      : null);
  if (preview === null || full === null) return null;
  const id =
    typeof entry.id === 'string' || typeof entry.id === 'number'
      ? String(entry.id)
      : (entry.slug ?? full.url ?? '');
  if (id === '') return null;
  return {
    id,
    previewUrl: preview.url ?? full.url ?? '',
    fullUrl: full.url ?? preview.url ?? '',
    width: full.width ?? preview.width ?? 240,
    height: full.height ?? preview.height ?? 180,
    altText: entry.title ?? entry.description ?? undefined,
  };
}

function extractItems(body: KlipyEnvelope): readonly KlipyItem[] {
  const data = body.data;
  if (data === undefined || data === null) return [];
  if (Array.isArray(data)) return data as readonly KlipyItem[];
  // `data` is the object-shaped variant — narrow once and pluck the
  // first array-valued field we recognize.
  const obj = data as {
    readonly data?: readonly KlipyItem[];
    readonly results?: readonly KlipyItem[];
    readonly items?: readonly KlipyItem[];
  };
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj.results)) return obj.results;
  if (Array.isArray(obj.items)) return obj.items;
  return [];
}

async function fetchKlipy(url: string, event: string): Promise<readonly GifItem[]> {
  if (keyValid === false) return [];
  try {
    const resp = await fetch(url, { method: 'GET' });
    if (!resp.ok) {
      if (resp.status === 400 || resp.status === 401 || resp.status === 403) {
        keyValid = false;
        log.warn({
          event: `${event}.key_invalid`,
          status: resp.status,
          hint: 'KLIPY returned an auth error. Get a free key at https://partner.klipy.com/api-keys and set VITE_KLIPY_API_KEY in apps/web/.env.',
        });
      } else {
        log.warn({ event: `${event}.http_error`, status: resp.status });
      }
      return [];
    }
    keyValid = true;
    const body = (await resp.json()) as KlipyEnvelope;
    if (body.result === false) {
      log.warn({
        event: `${event}.api_error`,
        errors: body.errors?.message ?? [],
      });
      return [];
    }
    return extractItems(body)
      .map((entry) => toGifItem(entry))
      .filter((entry): entry is GifItem => entry !== null);
  } catch (err) {
    log.warn({ event: `${event}.failed`, err: (err as Error).message });
    return [];
  }
}

/**
 * Build a `GifSearchProvider` backed by the KLIPY v1 REST API.
 * Returns `emptyGifProvider` when no API key is set so the panel
 * still draws an empty state.
 */
export function createKlipyGifProvider(): GifSearchProvider {
  const apiKey = readKlipyKey();
  if (apiKey === null) {
    log.info({ event: 'klipy.no_key' });
    return emptyGifProvider;
  }
  return {
    async trending(opts?: { limit?: number }): Promise<readonly GifItem[]> {
      const limit = clampLimit(opts?.limit);
      const url =
        `${KLIPY_BASE}/${encodeURIComponent(apiKey)}/gifs/trending` +
        `?${buildKlipyQuery({ limit })}`;
      return fetchKlipy(url, 'klipy.gif.trending');
    },
    async search(query: string, opts?: { limit?: number }): Promise<readonly GifItem[]> {
      const trimmed = query.trim();
      if (trimmed.length === 0) return [];
      const limit = clampLimit(opts?.limit);
      const url =
        `${KLIPY_BASE}/${encodeURIComponent(apiKey)}/gifs/search` +
        `?${buildKlipyQuery({ limit, q: trimmed })}`;
      return fetchKlipy(url, 'klipy.gif.search');
    },
  };
}

/**
 * Build a `StickerSearchProvider` backed by the KLIPY v1 sticker
 * endpoints. Returns `emptyStickerProvider` when no API key is set.
 *
 * Stickers endpoints mirror the GIF surface but take the documented
 * KLIPY shape:
 *   GET /api/v1/{key}/stickers/trending?page=&per_page=&customer_id=&locale=
 *   GET /api/v1/{key}/stickers/search  ?q=&page=&per_page=&customer_id=&locale=
 *
 * `customer_id` is a stable per-browser uuid persisted to
 * localStorage; `locale` defaults to `navigator.language`. See
 * `buildKlipyQuery` for the shared query construction.
 */
export function createKlipyStickerProvider(): StickerSearchProvider {
  const apiKey = readKlipyKey();
  if (apiKey === null) {
    log.info({ event: 'klipy.sticker.no_key' });
    return emptyStickerProvider;
  }
  return {
    async trending(opts?: { limit?: number }): Promise<readonly GifItem[]> {
      const limit = clampLimit(opts?.limit);
      const url =
        `${KLIPY_BASE}/${encodeURIComponent(apiKey)}/stickers/trending` +
        `?${buildKlipyQuery({ limit })}`;
      return fetchKlipy(url, 'klipy.sticker.trending');
    },
    async search(query: string, opts?: { limit?: number }): Promise<readonly GifItem[]> {
      const trimmed = query.trim();
      if (trimmed.length === 0) return [];
      const limit = clampLimit(opts?.limit);
      const url =
        `${KLIPY_BASE}/${encodeURIComponent(apiKey)}/stickers/search` +
        `?${buildKlipyQuery({ limit, q: trimmed })}`;
      return fetchKlipy(url, 'klipy.sticker.search');
    },
  };
}
